import React, { useRef, useEffect } from 'react';

// added the following 6 lines.
import mapboxgl, { LngLat, LngLatLike } from 'mapbox-gl';

// The following is required to stop "npm build" from transpiling mapbox code.
// notice the exclamation point in the import.
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_API_KEY;

function Map({ resultSet }: any) {
  const mapboxElRef = useRef(null); //DOM element to render map

  const coordinates = [
    [
      [115.744, -32.0558],
      [108.2813, -33.4314],
      [101.25, -34.3071],
      [92.6367, -35.2456],
      [86.6602, -35.6037],
      [78.4863, -35.6037],
      [70.1367, -34.8138],
      [62.3145, -34.0891],
      [55.8105, -32.8427],
      [48.6035, -31.2034],
      [43.3301, -29.6881],
      [40.75, -26.037],
      [37.75, -24.037],
      [34.75, -24.037],
      [31.75, -24.037],
    ],
  ];

  const data = resultSet?.map((data: any, index: number) => {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: coordinates[0][index],
      },
      properties: {
        id: index,
        date: data.date,
        dailySize: data.size,
        dailyColor: data.colour,
      },
    };
  }) || [];

  // Styles consistent with dashboard design system
  const mapStyles = {
    container: {
      flex: 1,
      minHeight: '300px',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative' as const,
      backgroundColor: '#f5f5f5',
      border: '1px solid #f0f0f0',
    },
    mapElement: {
      width: '100%',
      height: '100%',
      borderRadius: '8px',
    },
    loadingOverlay: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(245, 245, 245, 0.9)',
      color: '#8c8c8c',
      fontSize: '14px',
      zIndex: 1000,
    },
    errorState: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      color: '#8c8c8c',
      padding: '2rem',
      textAlign: 'center' as const,
    },
    errorIcon: {
      fontSize: '2rem',
      marginBottom: '1rem',
      color: '#ff4d4f',
    }
  };

  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      console.error('Mapbox token is required');
      return;
    }

    if (!resultSet || resultSet.length === 0) {
      return;
    }

    const map = new mapboxgl.Map({
      container: mapboxElRef.current!,
      accessToken: MAPBOX_TOKEN,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [78.4863, -29.6037],
      zoom: 2,
      maxZoom: 6,
      minZoom: 1,
      attributionControl: false, // Clean look
    });

    // Add navigation control with custom styling
    const navControl = new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true,
      visualizePitch: false
    });
    map.addControl(navControl, 'top-right');

    map.once('load', function () {
      map.addSource('points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: data,
        },
      });

      // Enhanced circle styling to match dashboard colors
      map.addLayer({
        id: 'circles',
        source: 'points',
        type: 'circle',
        paint: {
          'circle-opacity': 0.8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'dailySize'],
            -25, 6,
            0, 10,
            100, 18,
          ],
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'dailyColor'],
            -25, '#FF4500', // Red for poor conditions
            0, '#FFBB28',   // Orange for moderate
            100, '#32CD32', // Green for good conditions
          ],
        },
      });

      // Enhanced popup styling
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'custom-popup',
        offset: 15,
      });

      let lastId: any;

      map.on('mouseover', 'circles', (e) => {
        const features = e.features ? e.features[0] : null;
        const id = features?.properties?.id;

        if (id !== lastId) {
          lastId = id;
          const { date, dailyColor, dailySize }: any = features?.properties;

          map.getCanvas().style.cursor = 'pointer';
          var coordinates = null;
          if (features?.geometry.type === 'Point') {
            coordinates = features.geometry.coordinates.slice();

            // Enhanced popup HTML with dashboard styling
            const HTML = `
              <div style="
                padding: 0.75rem;
                font-family: 'Segoe UI', system-ui, sans-serif;
                font-size: 0.875rem;
                line-height: 1.4;
                min-width: 160px;
              ">
                <div style="
                  font-weight: 600;
                  color: #262626;
                  margin-bottom: 0.5rem;
                  padding-bottom: 0.5rem;
                  border-bottom: 1px solid #f0f0f0;
                ">
                  ${date}
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 0.25rem;">
                  <span style="color: #8c8c8c;">Daily Score:</span>
                  <span style="font-weight: 600; color: #262626;">${dailyColor.toFixed(1)}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #8c8c8c;">Cumulative:</span>
                  <span style="font-weight: 600; color: #262626;">${dailySize.toFixed(1)}</span>
                </div>
              </div>
            `;

            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
              coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            popup.setLngLat(coordinates as LngLatLike).setHTML(HTML).addTo(map);
          }
        }
      });

      map.on('mouseleave', 'circles', function () {
        lastId = undefined;
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
    });

    // Cleanup function
    return () => {
      if (map) {
        map.remove();
      }
    };
  }, [data]);

  // Loading state
  if (!resultSet || resultSet.length === 0) {
    return (
      <div style={mapStyles.container}>
        <div style={mapStyles.loadingOverlay}>
          Loading crew condition data...
        </div>
      </div>
    );
  }

  // Error state for missing token
  if (!MAPBOX_TOKEN) {
    return (
      <div style={mapStyles.container}>
        <div style={mapStyles.errorState}>
          <div style={mapStyles.errorIcon}>⚠️</div>
          <div>Mapbox configuration required</div>
          <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.7 }}>
            Please check your environment variables
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={mapStyles.container}>
      <div
        ref={mapboxElRef}
        style={mapStyles.mapElement}
      />
      
      {/* Custom styles for Mapbox popup */}
      <style>{`
        .mapboxgl-popup {
          max-width: 240px;
        }
        
        .mapboxgl-popup-content {
          padding: 0;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          border: 1px solid #f0f0f0;
        }
        
        .mapboxgl-popup-tip {
          border-top-color: #ffffff;
        }
        
        .mapboxgl-ctrl-group {
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid #f0f0f0;
        }
        
        .mapboxgl-ctrl-group button {
          width: 32px;
          height: 32px;
          border: none;
          background-color: #ffffff;
          color: #262626;
          transition: all 0.2s ease;
        }
        
        .mapboxgl-ctrl-group button:hover {
          background-color: #f5f5f5;
          color: #1890ff;
        }
        
        .mapboxgl-ctrl-group button:not(:last-child) {
          border-bottom: 1px solid #f0f0f0;
        }
        
        @media (prefers-color-scheme: dark) {
          .mapboxgl-popup-content {
            background-color: #1f1f1f;
            border-color: #434343;
            color: #ffffff;
          }
          
          .mapboxgl-popup-tip {
            border-top-color: #1f1f1f;
          }
          
          .mapboxgl-ctrl-group {
            background-color: #1f1f1f;
            border-color: #434343;
          }
          
          .mapboxgl-ctrl-group button {
            background-color: #1f1f1f;
            color: #ffffff;
          }
          
          .mapboxgl-ctrl-group button:hover {
            background-color: #2f2f2f;
            color: #69c0ff;
          }
        }
      `}</style>
    </div>
  );
}

export default Map;